"use client";

import type { ComponentType } from "react";
import { useSyncExternalStore } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AppleLogo,
  Check,
  ChevronDown,
  GameController,
  Moon,
  Sun,
} from "@/components/ui/icons";
import {
  applyAppTheme,
  getAppliedAppTheme,
  subscribeToAppTheme,
  type AppTheme,
} from "@/lib/appTheme";
import { cn } from "@/lib/utils";

type AppThemeMenuProps = {
  className?: string;
  showLabel?: boolean;
};

type ThemeIconProps = {
  className?: string;
  "aria-hidden"?: boolean;
};

const themeOptions: Array<{
  label: string;
  value: AppTheme;
  Icon: ComponentType<ThemeIconProps>;
}> = [
  { label: "Light", value: "light", Icon: Sun },
  { label: "Dark", value: "dark", Icon: Moon },
  { label: "PSX", value: "psone", Icon: GameController },
  { label: "System 6", value: "system", Icon: AppleLogo },
];

function getServerThemeSnapshot(): AppTheme {
  return "light";
}

export default function AppThemeMenu({
  className,
  showLabel = false,
}: AppThemeMenuProps) {
  const theme = useSyncExternalStore(
    subscribeToAppTheme,
    getAppliedAppTheme,
    getServerThemeSnapshot,
  );

  const activeTheme =
    themeOptions.find(({ value }) => value === theme) ?? themeOptions[0];
  const ActiveIcon = activeTheme.Icon;
  const activeLabel = activeTheme.label;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Choose UI style. Current style: ${activeLabel}`}
          title={`UI style: ${activeLabel}`}
          className={cn(
            "theme-button-secondary inline-flex items-center justify-center gap-2 rounded-full border transition-[transform,background,color,border-color,box-shadow] duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]",
            showLabel ? "min-h-10 px-3 text-sm font-semibold" : "size-9",
            className,
          )}
        >
          <ActiveIcon className={cn(showLabel ? "size-4" : "size-[0.95rem]")} />
          {showLabel ? (
            <>
              <span>{activeLabel}</span>
              <ChevronDown className="ml-auto size-3.5 text-muted-foreground" />
            </>
          ) : null}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-52 p-1.5"
        data-theme-menu=""
      >
        <DropdownMenuLabel className="px-2 py-1.5 text-xs uppercase tracking-[0.12em] text-muted-foreground">
          UI style
        </DropdownMenuLabel>
        {themeOptions.map(({ label, value, Icon }) => {
          const selected = theme === value;

          return (
            <DropdownMenuItem
              key={value}
              onClick={() => applyAppTheme(value)}
              aria-current={selected ? "true" : undefined}
              className="min-h-10 px-2.5 py-2"
            >
              <Icon className="size-4" />
              <span className="flex-1 font-medium">{label}</span>
              {selected ? (
                <Check className="size-4 text-[color:var(--color-primary)]" />
              ) : null}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled
          className="min-h-10 justify-between px-2.5 py-2"
        >
          <span className="font-medium">More UIs</span>
          <span className="text-xs font-semibold text-muted-foreground">
            Coming soon
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
